<template>
  <div class="my-2">
    <div class="my-3 pt-4">
      <h1 class="text-center mb-3">IT Project Quotation AI</h1>
      <div class="row text-in-row my-2 py-3 px-2 mx-1">
        <p>
          The IT Project Quotation AI helps me create a quotation draft for your IT project.
          This chat-based tool uses AI to gather project specifics.
          Describe your project, and the AI asks clarifying questions.
          Once your project is clear, the tool generates a quotation template, detailing deliverables, and T-shirt sizing for effort.
          Finally, you can request the project from me.
        </p>
        <p>
          Please note that these quotation drafts do not grant any rights and may contain mistakes due to the AI: I will manually review each one and send a final quotation.
          Use it to quickly define your project and request a quote from me.
          Actually, you can also use this tool without requesting a quotation from me: you can use it as a sparring partner too.
        </p>
        <span>Take a look at the <a href="/project-catalog">Project Catalog</a> for inspiration. For further questions, don't hesitate to reach out via the <a href="/contact">contact page</a>.</span>
      </div>
      <div class="example-quote-toggle mx-1"
           @click="showExample = !showExample"
           v-if="!quotation">
        <h6 class="toggle-link">> {{ showExample ? 'Hide' : 'Show' }} example project quotation</h6>
      </div>
      <transition name="fade" class="mx-1 quotation px-3"
                  v-if="!quotation">
        <div v-if="showExample" class="example-quote-table-wrapper">
          <table class="example-quote-table">
            <caption class="caption">Project: Migrate an existing on-premise PHP e-commerce platform to a scalable, cost-effective, and secure Python/AWS microservices architecture.</caption>
            <thead>
              <tr>
                <th>Deliverable</th>
                <th>Description</th>
                <th>Effort</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Discovery & Planning</td>
                <td>Requirements gathering, current system assessment, and new AWS architecture design.</td>
                <td>S</td>
              </tr>
              <tr>
                <td>Migration & Development</td>
                <td>Decomposition of monolithic PHP into Python microservices, data migration to AWS RDS/DynamoDB, containerization for AWS Fargate/ECS, CI/CD pipeline implementation.</td>
                <td>XL</td>
              </tr>
              <tr>
                <td>Performance Optimization & Cost Reduction</td>
                <td>Design and implement performance enhancements (CloudFront, ELB, caching) and cost reduction strategies (rightsizing, serverless).</td>
                <td>M</td>
              </tr>
              <tr>
                <td>Workshops & Handover</td>
                <td>Conduct workshops on the new architecture, AWS best practices, and application security for the client's internal team.</td>
                <td>S</td>
              </tr>
            </tbody>
          </table>
        </div>
      </transition>
      <div class="mx-1 quotation px-3 py-3"
           v-if="quotation">
        <h1>Quotation draft</h1>
        <div class="example-quote-table-wrapper">
          <table class="example-quote-table">
            <caption class="caption">Project: {{ quotation.goal }}</caption>
            <thead>
              <tr>
                <th>Deliverable</th>
                <th>Description</th>
                <th>Effort</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in quotation['work_items']" :key="index">
                <td>{{item.deliverable}}</td>
                <td>{{item.description}}</td>
                <td>{{item['t_shirt_size']}}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="btn-group d-flex w-100 mt-3">
          <div class="mb-3 mt-0 w-100">
            <router-link :to="'/project-quotation-ai/request/uuid/'+quotation_uuid">
              <button type="button"
                      class="btn btn-outline-light w-100">
                Request this project
              </button>
            </router-link>
          </div>
        </div>
      </div>
      <div class="chat-container mt-3 mx-1"
           v-if="!quotation && !loading_websockets">
        <div class="chat-window">
          <div v-for="(msg, idx) in messages" :key="idx" :class="['chat-message', msg.role]">
            <span class="sender">{{ msg.role === 'user' ? 'You' : 'AI' }}:</span>
            <span class="message">{{ msg.text }}</span>
          </div>
          <div class="d-flex justify-content-center align-items-center"
               v-if="loading_message">
            <div class="spinner-border m-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <span v-if="loading_message_timer_exceeded"
                  class="mx-2">
                  AI is taking longer than usual, please wait...
            </span>
          </div>
        </div>
        <form class="chat-input-row" @submit.prevent="sendMessage">
          <textarea
            v-model="userInput"
            ref="chatTextarea"
            class="chat-input chat-textarea"
            placeholder="Type your message..."
            autocomplete="off"
            rows="3"
            @input="autoResize"
            @keydown.enter="handleEnter"
            style="resize: none; min-height: 44px; max-height: 160px; overflow-y: auto;"
          ></textarea>
          <button class="send-btn" type="submit" :disabled="!userInput.trim()">Send</button>
        </form>
      </div>
      <div class="d-flex justify-content-center align-items-center"
            v-if="loading_websockets">
        <div class="spinner-border m-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { get_ws_connection } from '@/backend/backend.js'

  export default {
    data() {
      return {
        messages: [
          { role: 'ai', text: 'Hi! Please describe your IT project, and I will generate a quotation draft. What is the goal of the project? What does your IT currently look like?' }
        ],
        userInput: '',
        showExample: false,
        loading_message: false,
        loading_message_timer_exceeded: false,
        loading_websockets: true,
        quotation: null,
        wss_connection: null,
        quotation_uuid: null,
      }
    },
    methods: {
      sendMessage() {
        const input = this.userInput.trim();
        if (!input) return

        this.messages.push({ role: 'user', text: input });
        this.userInput = '';
        this.$nextTick(this.autoResize); // Reset textarea height

        this.loading_message = true
        this.loading_message_timer_exceeded = false
        setTimeout(() => {
          this.loading_message_timer_exceeded = true
        }, 5000)
        this.wss_connection.send(JSON.stringify({messages: this.messages}))
        this.$nextTick(() => {
          const chatWindow = this.$el.querySelector('.chat-window');
          chatWindow.scrollTop = chatWindow.scrollHeight;
        });
      },
      handleEnter(e) {
        if (!e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
        // else allow newline
      },
      autoResize() {
        const ta = this.$refs.chatTextarea;
        if (ta) {
          ta.style.height = 'auto';
          ta.style.height = ta.scrollHeight + 'px';
        }
      },
      getAIResponse(userText) {
        console.log(userText)
        return 'Thank you for the information! Are there any specific deliverables or deadlines you have in mind?';
      },
    },
    created () {
      document.title = 'IT Project Quotation AI'
    },
    mounted () {
      this.wss_connection = get_ws_connection('')
      this.wss_connection.onopen = () => {
        this.loading_websockets = false;
      };
      this.wss_connection.onmessage = (event) => {
        const body = JSON.parse(JSON.parse(event.data).body);
        console.log(body)
        if (body.quotation) {
          this.quotation = body.quotation
          this.quotation_uuid = body.uuid
        } else if (body.followup) {
          this.messages.push({ role: 'ai', text: body.followup });
        }
        this.loading_message = false;
      };
    }
  }
</script>

<style scoped>
  .text-in-row {
    text-align: left;
    background-color: rgb(43, 43, 43);
    border-radius: 10px;
  }
  .quotation {
    background-color: rgb(43, 43, 43);
    border-radius: 10px;
  }
  .chat-container {
    margin: 0 auto;
    background: rgb(43, 43, 43);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
    padding: 0 0 16px 0;
    display: flex;
    flex-direction: column;
  }
  .chat-window {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: transparent;
  }
  .chat-message {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 2px;
    font-size: 1rem;
    line-height: 1.4;
  }
  .chat-message.user {
    justify-content: flex-end;
    color: #ddd;
    background: #222;
    border-radius: 8px 8px 2px 8px;
    padding: 6px 12px;
    align-self: flex-end;
    max-width: 80%;
  }
  .chat-message.ai {
    justify-content: flex-start;
    color: #ddd;
    background: #191919;
    border-radius: 8px 8px 8px 2px;
    padding: 6px 12px;
    align-self: flex-start;
    max-width: 80%;
  }
  .sender {
    font-weight: bold;
    margin-right: 4px;
    text-align: left;
  }
  .message {
    text-align: left;
    white-space: pre-wrap;
  }
  .chat-input-row {
    display: flex;
    gap: 8px;
    padding: 8px 16px 0 16px;
    align-items: flex-end;
    background: rgb(43, 43, 43);
    border-radius: 0 0 12px 12px;
    border-top: 1px solid #333;
  }
  .chat-input.chat-textarea {
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.5;
    min-height: 44px;
    max-height: 160px;
    resize: none;
    overflow-y: auto;
    background: #222;
    color: #ddd;
    border: 1px solid #444;
    border-radius: 8px;
    padding: 10px 12px;
    width: 100%;
    box-sizing: border-box;
    transition: border 0.2s;
  }
  .chat-input.chat-textarea:focus {
    border: 1.5px solid #1976d2;
    outline: none;
  }
  .send-btn {
    background: #1976d2;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 10px 10px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s;
    min-height: 44px;
    min-width: 70px;
    font-weight: 500;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  }
  .send-btn:disabled {
    background: #444;
    cursor: not-allowed;
    color: #aaa;
  }
  .send-btn:hover:not(:disabled) {
    background: #1565c0;
  }
  .example-quote-toggle {
    margin: 18px 0 0 0;
    text-align: left;
    cursor: pointer;
    user-select: none;
  }
  .toggle-link {
    color: #ddd;
    text-decoration: underline;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s;
  }
  .toggle-link:hover {
    color: #aaa;
  }
  .example-quote-table-wrapper {
    margin: 16px 0 0 0;
    overflow-x: auto;
  }
  .example-quote-table {
    width: 100%;
    border-collapse: collapse;
    background: rgb(43, 43, 43);
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    font-size: 0.98rem;
    color: #ddd;
    margin-bottom: 0;
  }
  .example-quote-table th, .example-quote-table td {
    padding: 10px 14px;
    border-bottom: 1px solid #333;
    text-align: left;
  }
  .example-quote-table th {
    background: #191919;
    font-weight: 600;
    color: #ddd;
    border-top: 1px solid #333;
  }
  .example-quote-table tr:last-child td {
    border-bottom: none;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  .caption {
    font-size: 1.1rem;
    font-weight: 600;
    color: #ddd;
    text-align: left;
    caption-side: top;
  }
</style>
